/**
 * Shared inventory management panel.
 *
 * This is the core inventory management UI. Its sole purpose is to let
 * an operator perform receive / deduct / count operations on products in
 * a storage. It is used in exactly two places with identical UX:
 *
 *   1. In-app admin inventory management page (`InventoryActionPage`)
 *   2. Standalone warehouse kiosk page (`KioskInventoryPage`)
 *
 * The panel gates on `storage_config.enabled` (primary — always required).
 * Barcode scan input is additively gated on `barcodeEnabled` — when false,
 * the panel works in manual-only mode with product autocomplete.
 *
 * Props:
 *   - storageId:      fixed storage (kiosk) — if null and `storages`
 *                     provided, the panel shows a selector
 *   - storages:       array of storages for the selector (null = locked)
 *   - allowedModes:   subset of ['receive','deduct','count']
 *   - apiClient:      axios instance (JWT or kiosk-token)
 *   - barcodeEnabled: show gun-scan input + Print Labels button
 *   - adjustEndpoint: '/inventory/adjust/' (admin) or '/kiosk/inventory/adjust/' (kiosk)
 *   - lookupEndpoint: '/barcode/lookup/' (admin) or '/kiosk/products/lookup/' (kiosk)
 *   - productAutocompleteEndpoint: '/products/' (admin) or '/kiosk/products/lookup/' (kiosk)
 *   - canPrintLabels: kiosk=false, admin=depends on role
 *   - productLabel:   terminology override
 *   - onSubmitSuccess(results): optional callback after successful commit
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  IconButton,
  Typography,
  Alert,
  Chip,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Divider,
  Collapse,
  Dialog,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PrintIcon from '@mui/icons-material/Print';
import CheckIcon from '@mui/icons-material/Check';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InventoryIcon from '@mui/icons-material/Inventory';
import Tooltip from '@mui/material/Tooltip';
import BarcodePrintDialog from '../dialogs/BarcodePrintDialog';
import BarcodeScannerDialog from '../dialogs/BarcodeScannerDialog';

const MODE_LABELS = {
  receive: 'קבלה',
  deduct: 'לקיחה',
  count: 'ספירה',
};
const MODE_COLORS = {
  receive: 'success',
  deduct: 'error',
  count: 'info',
};

/**
 * Build the mode-specific bright background tint for the action bar.
 */
const modeBg = (mode) =>
  ({
    receive: 'rgba(16, 185, 129, 0.08)',
    deduct: 'rgba(239, 68, 68, 0.08)',
    count: 'rgba(59, 130, 246, 0.08)',
  }[mode] || 'transparent');

const InventoryActionPanel = ({
  storageId = null,
  storages = null,
  allowedModes = ['receive', 'deduct', 'count'],
  apiClient,
  barcodeEnabled = false,
  adjustEndpoint = '/inventory/adjust/',
  lookupEndpoint = '/barcode/lookup/',
  productAutocompleteEndpoint = '/products/',
  canPrintLabels = false,
  productLabel = 'מוצר',
  onSubmitSuccess,
  // Opt-in: show a camera-scan IconButton next to the barcode input.
  // Admins get it on InventoryActionPage; the kiosk leaves it off because
  // it authenticates with a kiosk token (not JWT) and relies on a USB gun.
  cameraScanEnabled = false,
}) => {
  const [mode, setMode] = useState(allowedModes[0] || 'receive');
  const [selectedStorageId, setSelectedStorageId] = useState(
    storageId || (storages?.find((s) => s.is_default) || storages?.[0])?.StorageID || null
  );
  const [items, setItems] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [autocompleteSearch, setAutocompleteSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [cameraScannerOpen, setCameraScannerOpen] = useState(false);
  const [recentHistory, setRecentHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [stockSearch, setStockSearch] = useState('');
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [stockResults, setStockResults] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const stockDebounceRef = useRef(null);

  const scanInputRef = useRef(null);
  const autocompleteDebounceRef = useRef(null);

  // Always keep the scan input focused — operators move fast between scans.
  const refocus = useCallback(() => {
    setTimeout(() => {
      if (scanInputRef.current) scanInputRef.current.focus();
    }, 50);
  }, []);

  // Focus on mount + after every state change that clears items
  useEffect(() => {
    refocus();
  }, [refocus, items.length, mode]);

  // Ensure focus on initial render
  useEffect(() => {
    refocus();
  }, [refocus]);

  // ─── Row management ──────────────────────────────────────────────
  // Fetch current stock for a product+storage when not already known
  const fetchCurrentStock = useCallback(async (productId, storageId) => {
    try {
      const res = await apiClient.get('/inventory/product-stock/', {
        params: { product_id: productId, storage_id: storageId },
      });
      return res.data?.quantity ?? '0';
    } catch {
      return '0';
    }
  }, [apiClient]);

  // ─── Stock search (read-only) ─────────────────────────────────
  useEffect(() => {
    if (!stockSearch || stockSearch.length < 2) {
      setStockResults([]);
      return;
    }
    if (stockDebounceRef.current) clearTimeout(stockDebounceRef.current);
    stockDebounceRef.current = setTimeout(async () => {
      setStockLoading(true);
      try {
        const isKiosk = productAutocompleteEndpoint.includes('/kiosk/');
        const res = await apiClient.get(productAutocompleteEndpoint, {
          params: isKiosk ? { q: stockSearch, limit: 10 } : undefined,
        });
        let list = [];
        if (isKiosk) {
          list = res.data?.results || [];
        } else {
          const all = Array.isArray(res.data) ? res.data : (res.data?.results || []);
          const q = stockSearch.toLowerCase();
          list = all
            .filter((p) =>
              (p.ProductName || '').toLowerCase().includes(q) ||
              (p.CatalogNumber || '').toLowerCase().includes(q) ||
              (p.Barcode || '').toLowerCase().includes(q)
            )
            .slice(0, 10);
        }
        if (!isKiosk && selectedStorageId) {
          const withStock = await Promise.all(
            list.map(async (p) => {
              const qty = await fetchCurrentStock(p.ProductID, selectedStorageId);
              return { ...p, current_quantity: qty };
            })
          );
          setStockResults(withStock);
        } else {
          setStockResults(list);
        }
      } catch {
        setStockResults([]);
      } finally {
        setStockLoading(false);
      }
    }, 400);
    return () => { if (stockDebounceRef.current) clearTimeout(stockDebounceRef.current); };
  }, [stockSearch, apiClient, productAutocompleteEndpoint, selectedStorageId, fetchCurrentStock]);

  const addItem = useCallback((product) => {
    setItems((prev) => {
      const existing = prev.find((r) => r.ProductID === product.ProductID);
      if (existing) {
        return prev.map((r) =>
          r.ProductID === product.ProductID
            ? { ...r, quantity: (parseFloat(r.quantity || 0) + 1).toString() }
            : r
        );
      }
      return [
        {
          ProductID: product.ProductID,
          ProductName: product.ProductName,
          Barcode: product.Barcode || '',
          CatalogNumber: product.CatalogNumber || '',
          Price: product.Price || null,
          current_quantity: product.current_quantity ?? null,
          thumbnail: product.primary_image_thumbnail || '',
          quantity: '1',
        },
        ...prev,
      ];
    });
    // If current_quantity wasn't provided, fetch it from the backend
    if (product.current_quantity == null && selectedStorageId) {
      fetchCurrentStock(product.ProductID, selectedStorageId).then((qty) => {
        setItems((prev) =>
          prev.map((r) =>
            r.ProductID === product.ProductID && r.current_quantity == null
              ? { ...r, current_quantity: qty }
              : r
          )
        );
      });
    }
  }, [selectedStorageId, fetchCurrentStock]);

  // ─── Scan input → lookup ────────────────────────────────────────
  const scanLookupRef = useRef(false); // prevent double-submit
  const scanDebounceRef = useRef(null);

  const doScanLookup = useCallback(async (barcode) => {
    if (!barcode || scanLookupRef.current) return;
    scanLookupRef.current = true;
    try {
      const params = { barcode };
      if (selectedStorageId) params.storage_id = selectedStorageId;
      const res = await apiClient.get(lookupEndpoint, { params });
      const product = res.data?.product || res.data?.results?.[0] || null;
      if (!product) {
        setSnackbar({
          open: true,
          message: `לא נמצא מוצר לברקוד: ${barcode}`,
          severity: 'error',
        });
        setScanInput('');
        return;
      }
      addItem(product);
      setScanInput('');
    } catch (err) {
      const status = err?.response?.status;
      let message;
      if (status === 404) message = `לא נמצא מוצר לברקוד: ${barcode}`;
      else if (status === 403) message = 'סריקת ברקוד אינה מאופשרת עבור הקבוצה';
      else if (status === 400) message = `ברקוד לא תקין: ${barcode}`;
      else if (!err?.response) message = 'שגיאת רשת. נסה שוב.';
      else message = `שגיאה בחיפוש הברקוד: ${barcode}`;
      setSnackbar({ open: true, message, severity: 'error' });
      setScanInput('');
    } finally {
      scanLookupRef.current = false;
      refocus();
    }
  }, [apiClient, lookupEndpoint, selectedStorageId, addItem, refocus]);

  // Auto-submit when a complete barcode is detected (13 digits for EAN-13,
  // or any 8+ digit string after 400ms of no typing — covers most formats).
  const handleScanChange = useCallback((value) => {
    setScanInput(value);
    if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);
    const trimmed = value.trim();
    if (trimmed.length >= 8 && /^\d+$/.test(trimmed)) {
      scanDebounceRef.current = setTimeout(() => {
        doScanLookup(trimmed);
      }, 400);
    }
  }, [doScanLookup]);

  // Enter key still works as immediate submit
  const handleScanKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);
    const raw = scanInput.trim();
    if (raw) doScanLookup(raw);
  }, [scanInput, doScanLookup]);

  // ─── Autocomplete search ─────────────────────────────────────────
  useEffect(() => {
    if (!autocompleteSearch || autocompleteSearch.length < 2) {
      setAutocompleteOptions([]);
      return;
    }
    if (autocompleteDebounceRef.current) clearTimeout(autocompleteDebounceRef.current);
    autocompleteDebounceRef.current = setTimeout(async () => {
      setAutocompleteLoading(true);
      try {
        // Kiosk lookup uses ?q=; admin /products/ uses no search param but
        // returns all products and we filter client-side.
        const isKioskLookup = productAutocompleteEndpoint.includes('/kiosk/');
        const res = await apiClient.get(productAutocompleteEndpoint, {
          params: isKioskLookup ? { q: autocompleteSearch, limit: 20 } : undefined,
        });
        let list = [];
        if (isKioskLookup) {
          list = res.data?.results || [];
        } else {
          const all = Array.isArray(res.data) ? res.data : (res.data?.results || []);
          const q = autocompleteSearch.toLowerCase();
          list = all
            .filter(
              (p) =>
                (p.ProductName || '').toLowerCase().includes(q) ||
                (p.CatalogNumber || '').toLowerCase().includes(q) ||
                (p.Barcode || '').toLowerCase().includes(q)
            )
            .slice(0, 20);
        }
        setAutocompleteOptions(list);
      } catch (err) {
        setAutocompleteOptions([]);
      } finally {
        setAutocompleteLoading(false);
      }
    }, 300);
    return () => {
      if (autocompleteDebounceRef.current) clearTimeout(autocompleteDebounceRef.current);
    };
  }, [autocompleteSearch, apiClient, productAutocompleteEndpoint]);

  const updateQty = (productId, value) => {
    setItems((prev) =>
      prev.map((r) => (r.ProductID === productId ? { ...r, quantity: value } : r))
    );
  };

  const incQty = (productId, delta) => {
    setItems((prev) =>
      prev.map((r) => {
        if (r.ProductID !== productId) return r;
        const curr = parseFloat(r.quantity || 0) || 0;
        return { ...r, quantity: Math.max(0, curr + delta).toString() };
      })
    );
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((r) => r.ProductID !== productId));
  };

  const clearAll = () => {
    setItems([]);
    refocus();
  };

  const itemCount = items.length;
  const totalUnits = items.reduce((sum, r) => sum + (parseFloat(r.quantity || 0) || 0), 0);

  // ─── Submit ──────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (itemCount === 0) {
      setSnackbar({ open: true, message: 'אין מוצרים לעדכון', severity: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      const rows = items.map((r) => ({
        ProductID: r.ProductID,
        mode,
        quantity: parseFloat(r.quantity || 0) || 0,
        barcode: r.Barcode || undefined,
      }));
      const body = {
        StorageID: selectedStorageId || undefined,
        rows,
      };
      const res = await apiClient.post(adjustEndpoint, body);
      setSnackbar({
        open: true,
        message: `עודכן בהצלחה (${rows.length} פריטים, ${totalUnits} יחידות)`,
        severity: 'success',
      });
      // Add to recent history for quick validation
      const historyEntry = {
        id: Date.now(),
        timestamp: new Date(),
        mode,
        items: items.map((r) => ({
          ProductName: r.ProductName,
          quantity: parseFloat(r.quantity || 0) || 0,
        })),
      };
      setRecentHistory((prev) => [historyEntry, ...prev].slice(0, 20));
      setShowHistory(true);
      setItems([]);
      if (onSubmitSuccess) onSubmitSuccess(res.data);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || 'שגיאה בעדכון המלאי',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
      refocus();
    }
  };

  const modeLabel = MODE_LABELS[mode] || mode;
  const modeColor = MODE_COLORS[mode] || 'primary';

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        bgcolor: modeBg(mode),
        transition: 'background-color 200ms ease',
      }}
    >
      {/* ── Storage selector (top, hidden if single storage) ───── */}
      {storages && storages.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>מחסן</InputLabel>
            <Select
              value={selectedStorageId || ''}
              label="מחסן"
              onChange={(e) => setSelectedStorageId(e.target.value)}
            >
              {storages.map((s) => (
                <MenuItem key={s.StorageID} value={s.StorageID}>
                  {s.StorageName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* ── Top input row ─────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, mb: 2 }}>
        {barcodeEnabled && (
          // Inner row: plain TextField for the USB-gun / keyboard-typed
          // barcode, and a separate camera IconButton next to it for
          // phone operators. Keeping them as siblings (not endAdornment)
          // means a gun scan hits a clean input (no accidental
          // interaction with the adornment button), and the camera
          // button is a clearly-separate affordance.
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', flex: 1, minWidth: 0 }}>
            <TextField
              inputRef={scanInputRef}
              autoFocus
              label="סרוק ברקוד"
              placeholder="הצמד אקדח סריקה או הקלד ברקוד"
              value={scanInput}
              onChange={(e) => handleScanChange(e.target.value)}
              onKeyDown={handleScanKeyDown}
              InputProps={{
                startAdornment: <QrCodeScannerIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                sx: { direction: 'ltr', fontSize: '1.1rem' },
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            {cameraScanEnabled && (
              <Tooltip title="סרוק באמצעות מצלמה">
                <IconButton
                  onClick={() => setCameraScannerOpen(true)}
                  aria-label="סרוק באמצעות מצלמה"
                  sx={{
                    flexShrink: 0,
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' },
                    borderRadius: 1,
                  }}
                >
                  <CameraAltIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
        <Autocomplete
          fullWidth
          options={autocompleteOptions}
          loading={autocompleteLoading}
          filterOptions={(x) => x}
          getOptionLabel={(opt) => opt?.ProductName || ''}
          onChange={(_e, value) => {
            if (value) {
              addItem(value);
              setAutocompleteSearch('');
              setTimeout(refocus, 0);
            }
          }}
          inputValue={autocompleteSearch}
          onInputChange={(_e, v) => setAutocompleteSearch(v)}
          noOptionsText={autocompleteSearch.length < 2 ? 'הזן לפחות 2 תווים' : 'לא נמצאו מוצרים'}
          renderOption={(props, opt) => (
            <li {...props} key={opt.ProductID}>
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1.5 }}>
                {opt.primary_image_thumbnail ? (
                  <Box
                    component="img"
                    src={opt.primary_image_thumbnail}
                    alt=""
                    sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {opt.ProductName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.CatalogNumber || ''} {opt.Barcode ? `· ${opt.Barcode}` : ''}
                  </Typography>
                </Box>
                {opt.current_quantity != null && (
                  <Chip
                    size="small"
                    icon={<InventoryIcon sx={{ fontSize: 14 }} />}
                    label={opt.current_quantity}
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`בחר ${productLabel} ידנית`}
              placeholder="הקלד שם או מק״ט"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ flex: 1 }}
        />
      </Box>

      {/* ── Mode toggle ────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          exclusive
          value={mode}
          onChange={(_e, v) => v && setMode(v)}
          size="large"
        >
          {allowedModes.includes('receive') && (
            <ToggleButton value="receive" color="success" sx={{ px: 3 }}>
              {MODE_LABELS.receive}
            </ToggleButton>
          )}
          {allowedModes.includes('deduct') && (
            <ToggleButton value="deduct" color="error" sx={{ px: 3 }}>
              {MODE_LABELS.deduct}
            </ToggleButton>
          )}
          {allowedModes.includes('count') && (
            <ToggleButton value="count" color="info" sx={{ px: 3 }}>
              {MODE_LABELS.count}
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ── Items list ─────────────────────────────────────────── */}
      {items.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            {barcodeEnabled ? `סרוק ברקוד או בחר ${productLabel} כדי להתחיל` : `בחר ${productLabel} כדי להתחיל`}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((r) => (
            <Paper
              key={r.ProductID}
              elevation={0}
              sx={{
                p: 1.5,
                border: '2px solid',
                borderColor: MODE_COLORS[mode] + '.main',
                borderRadius: 2,
                display: 'flex',
                gap: { xs: 1, sm: 2 },
                alignItems: 'center',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                bgcolor: modeBg(mode),
              }}
            >
              {r.thumbnail ? (
                <Box
                  component="img"
                  src={r.thumbnail}
                  alt=""
                  onClick={(e) => { e.stopPropagation(); setEnlargedImage(r.thumbnail.replace('/c_thumb,f_auto,g_center,h_150,q_auto,w_150/', '/f_auto,q_auto/')); }}
                  sx={{ width: 44, height: 44, borderRadius: 1, objectFit: 'cover', flexShrink: 0, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                />
              ) : (
                <Box sx={{ width: 44, height: 44, borderRadius: 1, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <InventoryIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {r.ProductName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.Barcode && <span style={{ direction: 'ltr' }}>{r.Barcode}</span>}
                  {r.CatalogNumber && ` · מק״ט: ${r.CatalogNumber}`}
                </Typography>
              </Box>
              {/* Current stock - highlighted */}
              <Box sx={{ textAlign: 'center', flexShrink: 0, px: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
                  {mode === 'count' ? 'במלאי' : 'מלאי נוכחי'}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: parseFloat(r.current_quantity) > 0 ? 'success.main' : 'error.main',
                    lineHeight: 1.2,
                  }}
                >
                  {r.current_quantity ?? '–'}
                </Typography>
              </Box>
              {/* Quantity controls — only for receive/deduct, count is read-only */}
              {mode !== 'count' && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexBasis: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'space-between', sm: 'flex-start' },
                }}>
                  <IconButton size="small" onClick={() => incQty(r.ProductID, -1)}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <TextField
                    size="small"
                    type="number"
                    value={r.quantity}
                    onChange={(e) => updateQty(r.ProductID, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    onWheel={(e) => e.target.blur()}
                    inputProps={{ min: 0, step: 1, style: { textAlign: 'center', width: 64, fontWeight: 700 } }}
                    sx={{
                      '& input': {
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        MozAppearance: 'textfield',
                      },
                    }}
                  />
                  <IconButton size="small" onClick={() => incQty(r.ProductID, 1)}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => removeItem(r.ProductID)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {mode === 'count' && (
                <Box sx={{
                  flexBasis: { xs: '100%', sm: 'auto' },
                  display: 'flex',
                  justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                }}>
                  <IconButton size="small" color="error" onClick={() => removeItem(r.ProductID)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {/* ── Sticky bottom action bar ───────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label={`${itemCount} פריטים`} />
          {mode !== 'count' && (
            <Chip label={`${totalUnits} יחידות`} color={modeColor} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {mode === 'receive' && barcodeEnabled && canPrintLabels && items.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => setPrintDialogOpen(true)}
              sx={{ direction: 'ltr' }}
            >
              הדפס מדבקות
            </Button>
          )}
          {items.length > 0 && (
            <Button variant="outlined" color="inherit" onClick={clearAll}>
              נקה
            </Button>
          )}
          {mode !== 'count' && (
            <Button
              variant="contained"
              color={modeColor}
              size="large"
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
              onClick={handleSubmit}
              disabled={submitting || itemCount === 0}
              sx={{ direction: 'ltr' }}
            >
              {submitting ? 'מעדכן...' : `אשר ועדכן מלאי (${modeLabel})`}
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Snackbar ───────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Recent history ─────────────────────────────────────── */}
      {recentHistory.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            bgcolor: '#fafbfc',
          }}
        >
          <Box
            onClick={() => setShowHistory((v) => !v)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              mb: showHistory ? 1.5 : 0,
            }}
          >
            <HistoryIcon color="action" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
              פעולות אחרונות ({recentHistory.length})
            </Typography>
            {showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          <Collapse in={showHistory}>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
              {recentHistory.map((entry) => (
                <Box
                  key={entry.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    px: 1,
                    borderBottom: '1px solid #f1f5f9',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Chip
                    size="small"
                    label={MODE_LABELS[entry.mode] || entry.mode}
                    color={MODE_COLORS[entry.mode] || 'default'}
                    sx={{ minWidth: 56, fontWeight: 600 }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {entry.items.map((it) => `${it.ProductName} ×${it.quantity}`).join(', ')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ direction: 'ltr', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                    {entry.timestamp.toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric' })} {entry.timestamp.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* ── Stock search (always visible) ─────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 2,
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          bgcolor: '#fafbfc',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <InventoryIcon color="action" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            בדיקת מלאי
          </Typography>
        </Box>
        <TextField
          size="small"
          fullWidth
          placeholder="חפש מוצר לבדיקת כמות במלאי..."
          value={stockSearch}
          onChange={(e) => setStockSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary' }} />,
          }}
          sx={{ mb: 1 }}
        />
        {stockLoading && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!stockLoading && stockResults.length > 0 && (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {stockResults.map((p) => (
              <Box
                key={p.ProductID}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1.25,
                  px: 1,
                  borderBottom: '1px solid #f1f5f9',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                {p.primary_image_thumbnail ? (
                  <Box
                    component="img"
                    src={p.primary_image_thumbnail}
                    alt=""
                    onClick={() => setEnlargedImage(p.primary_image_thumbnail.replace('/c_thumb,f_auto,g_center,h_150,q_auto,w_150/', '/f_auto,q_auto/'))}
                    sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover', flexShrink: 0, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                  />
                ) : (
                  <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <InventoryIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {p.ProductName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.CatalogNumber && `מק״ט: ${p.CatalogNumber}`}
                    {p.Barcode && <span style={{ direction: 'ltr' }}>{` · ${p.Barcode}`}</span>}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
                    במלאי
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: parseFloat(p.current_quantity) > 0 ? 'success.main' : 'error.main',
                      lineHeight: 1.2,
                    }}
                  >
                    {p.current_quantity ?? '–'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {!stockLoading && stockSearch.length >= 2 && stockResults.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            לא נמצאו מוצרים
          </Typography>
        )}
      </Paper>

      {/* ── Image lightbox ─────────────────────────────────────── */}
      <Dialog
        open={!!enlargedImage}
        onClose={() => setEnlargedImage(null)}
        maxWidth="md"
        PaperProps={{
          sx: { bgcolor: '#000', boxShadow: 24, overflow: 'hidden', position: 'relative' },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 0,
          }}
        >
          <CircularProgress sx={{ color: '#fff' }} />
        </Box>
        <Box
          component="img"
          src={enlargedImage || ''}
          alt=""
          onClick={() => setEnlargedImage(null)}
          onLoad={(e) => { e.target.style.opacity = 1; }}
          sx={{
            maxWidth: '90vw',
            maxHeight: '85vh',
            objectFit: 'contain',
            cursor: 'pointer',
            display: 'block',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            position: 'relative',
            zIndex: 1,
          }}
        />
      </Dialog>

      {/* ── Print labels dialog (admin only) ───────────────────── */}
      {canPrintLabels && (
        <BarcodePrintDialog
          open={printDialogOpen}
          onClose={() => setPrintDialogOpen(false)}
          initialProducts={items.map((r) => ({
            ProductID: r.ProductID,
            ProductName: r.ProductName,
            Barcode: r.Barcode,
            Price: r.Price,
          }))}
        />
      )}

      {/* ── Camera barcode scanner (phone/tablet, admin only) ── */}
      {barcodeEnabled && cameraScanEnabled && (
        <BarcodeScannerDialog
          open={cameraScannerOpen}
          onClose={() => {
            setCameraScannerOpen(false);
            refocus();
          }}
          autoAdd
          onAdd={({ product }) => addItem(product)}
          productLabel={productLabel}
        />
      )}
    </Paper>
  );
};

export default InventoryActionPanel;
